import { useEffect } from "react";
import { Input, Textarea } from "@chakra-ui/react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import createFormFieldComponents from "components/Shared/Forms/FormField";
import Form, { FormProps } from "components/Shared/Forms/Form";
import { Writeable } from "utils/typescriptUtilis";
import { gql } from "__generated__/gql";
import {
  RetailerFilter,
  RetailerInput,
  RetailerType,
} from "__generated__/gql/graphql";
import type { OptionalKeys } from "utils/typescriptUtilis";
import * as retailerSchemaFields from "utils/schemas/retailer";
import NumberInput from "components/Shared/Forms/NumberInput";
import RadioButtonOptionsSelect from "components/Shared/Forms/RadioButtonOptionsSelect";
import UrlInput from "components/Shared/Forms/UrlInput";
import AddressFieldset, {
  AddressFieldsetType,
} from "components/Shared/Forms/AddressFieldset";
import PhoneInput, {
  PhoneNumberFieldsetType,
} from "components/Shared/Forms/PhoneInput";
import {
  pricingStrategies,
  pricingStrategyMap,
  retailerCategories,
  retailerCategoryReviewMap,
  slottingOptions,
  slottingOptionsMap,
} from "components/Shared/RetailerDirectory/utils";
import BrokerSelect from "components/Shared/Select/BrokerSelect";
import RetailerChannelSelect from "components/Shared/Select/RetailerChannelSelect";
import RetailerRegionSelect from "components/Shared/Select/RetailerRegionSelect";
import DistributionCenterSelect from "components/Shared/Select/DistributionCenterSelect";
import RetailerTagsSelect from "components/Shared/Select/RetailerTagsSelect";
import SwitchInput from "components/Shared/Forms/SwitchInput";
import {
  getPostgraphileErrorMatchersForFields,
  makePostgraphileErrorProcessor,
} from "utils/PostgraphileErrorExtensions";
import {
  RETAILER_TYPE_LABELS,
  RETAILER_TYPE_OPTIONS,
} from "components/Shared/RetailerDirectory/RetailerTypeCheckboxes";
import RetailerSelect from "components/Shared/Select/RetailerSelect";

export const m2mFragment = gql(/* GraphQL */ `
  fragment UpdateRetailerManyToMany on Mutation {
    setChannelsForRetailer(
      input: { retailerId: $id, retailerChannelIds: $retailerChannelIds }
    ) {
      retailer {
        id
      }
    }
    setRegionsForRetailer(
      input: { retailerId: $id, retailerRegionIds: $retailerRegionIds }
    ) {
      retailer {
        id
      }
    }
    setBrokersForRetailer(input: { retailerId: $id, brokerIds: $brokerIds })
      @include(if: $isAdmin) {
      retailer {
        id
      }
    }
    setDistributionCentersForRetailer(
      input: { retailerId: $id, distributionCenterIds: $distributionCenterIds }
    ) {
      retailer {
        id
        ...EditRetailerDetails
        ...RetailerDetails
        ...RetailersDirectoryTableRow
      }
    }
  }
`);

export const retailerFields = [
  "name",
  "uniqueSuffix",
  "retailerType",
  "parentId",
  "website",
  "numStores",
  "description",
  "slottingRequired",
  "pricingStrategy",
  "categoryReview",
  "isAnchor",
  "affiliation",
  "tags",
  "notes",
] as const;

type PrimitiveFields = OptionalKeys<
  Required<Writeable<Pick<RetailerInput, typeof retailerFields[number]>>>,
  "isAnchor"
>;

export type RetailerFormFields = PrimitiveFields & {
  brokerIds?: UUID[];
  headquartersAddress: AddressFieldsetType;
  headquartersPhoneNumber: PhoneNumberFieldsetType;
  channelIds: UUID[];
  regionIds: UUID[];
  distributionCenterIds: UUID[];
};

type RetailerFormContext = { isAdmin?: boolean };

const schema: yup.ObjectSchema<RetailerFormFields> = yup.object().shape({
  name: retailerSchemaFields.name.required(),
  uniqueSuffix: retailerSchemaFields.uniqueSuffix,
  retailerType: retailerSchemaFields.retailerType,
  parentId: retailerSchemaFields.parentId.defined().default(null),
  website: retailerSchemaFields.website.defined().default(null),
  numStores: retailerSchemaFields.numStores.defined().default(null),
  description: retailerSchemaFields.description.defined().default(""),
  headquartersAddress: retailerSchemaFields.headquartersAddress,
  headquartersPhoneNumber: retailerSchemaFields.headquartersPhoneNumber,
  slottingRequired: retailerSchemaFields.slottingRequired
    .defined()
    .nullable()
    .default(null),
  pricingStrategy: retailerSchemaFields.pricingStrategy
    .defined()
    .nullable()
    .default(null),
  categoryReview: retailerSchemaFields.categoryReview
    .defined()
    .nullable()
    .default(null),
  channelIds: retailerSchemaFields.channelIds,
  regionIds: retailerSchemaFields.regionIds,
  distributionCenterIds: retailerSchemaFields.distributionCenterIds,
  brokerIds: retailerSchemaFields.brokerIds,
  isAnchor: retailerSchemaFields.isAnchor.when("$isAdmin", {
    is: true,
    then: (s) => s.required().default(false),
    otherwise: (s) => s.transform(() => undefined),
  }),
  affiliation: retailerSchemaFields.affiliation.default(null),
  tags: retailerSchemaFields.tags,
  notes: retailerSchemaFields.notes.defined().default(""),
});

const { FormField, ControlledFormField } =
  createFormFieldComponents<RetailerFormFields>({
    schema,
  });

export const processApolloError =
  makePostgraphileErrorProcessor<PrimitiveFields>(
    getPostgraphileErrorMatchersForFields<keyof PrimitiveFields>(
      retailerFields,
      {
        type: "Retailer",
      },
    ),
  );

export type RetailerFormProps = Pick<
  FormProps<RetailerFormFields, RetailerFormContext>,
  "onSubmit" | "onValidSubmit"
> & {
  defaultValues?: Partial<RetailerFormFields>;
  formId?: string;
  isAdmin?: boolean;
  isLoaded?: boolean;
  parentSelectFilter?: RetailerFilter;
};

export default function RetailerForm({
  defaultValues,
  formId = "retailer-form",
  isAdmin,
  isLoaded,
  onSubmit,
  onValidSubmit,
  parentSelectFilter,
}: RetailerFormProps) {
  const formContext = useForm<RetailerFormFields, RetailerFormContext>({
    context: { isAdmin },
    defaultValues: { retailerType: RetailerType.CHAIN },
    resolver: yupResolver(schema),
  });

  const { reset } = formContext;

  useEffect(() => {
    if (defaultValues) {
      reset(defaultValues);
    }
  }, [defaultValues, reset]);

  const retailerType = formContext.watch("retailerType");

  return (
    <Form {...{ formContext, onSubmit, onValidSubmit, isLoaded }} id={formId}>
      <FormField name="name" autoFocus />

      <FormField
        name="uniqueSuffix"
        renderInput={(props) => <Input {...props} size="sm" />}
        labelProps={{ fontSize: "sm", mb: 1 }}
        helpTextProps={{ fontSize: ".7em", mb: 1 }}
        helpText={
          <>
            This should normally remain empty, but can be used to help
            distinguish between retailers that otherwise have the same name.
          </>
        }
      />

      <ControlledFormField
        name="retailerType"
        onChange={(evt) => {
          if (!isAdmin) return;
          const newRetailerType = (evt.target as unknown as HTMLInputElement)
            .value as RetailerType;
          if (
            !newRetailerType ||
            !retailerSchemaFields.FLAG_RETAILER_TYPES.includes(newRetailerType)
          ) {
            formContext.setValue("isAnchor", false);
          }
          if (newRetailerType !== RetailerType.INDEPENDENT) {
            formContext.setValue("affiliation", null);
          }
        }}
        renderInput={({ field }) => (
          <RadioButtonOptionsSelect
            options={RETAILER_TYPE_OPTIONS}
            labels={RETAILER_TYPE_LABELS}
            size="sm"
            {...field}
          />
        )}
      />

      <ControlledFormField
        name="website"
        renderInput={({ field }) => <UrlInput {...field} />}
      />

      <ControlledFormField
        name="numStores"
        renderInput={({ field }) => (
          <NumberInput {...field} thousandSeparator decimalScale={0} min={0} />
        )}
      />

      <ControlledFormField
        name="parentId"
        renderInput={({ field }) => (
          <RetailerSelect
            {...field}
            isAdmin={isAdmin}
            isClearable
            filter={parentSelectFilter}
          />
        )}
      />

      <FormField name="description" InputComponent={Textarea} />

      {isAdmin && (
        <ControlledFormField
          name="isAnchor"
          renderInput={({ field }) => <SwitchInput {...field} />}
          isDisabled={
            !retailerType ||
            !retailerSchemaFields.FLAG_RETAILER_TYPES.includes(retailerType)
          }
        />
      )}

      <ControlledFormField
        name="channelIds"
        renderInput={({ field }) => (
          <RetailerChannelSelect
            {...field}
            isMulti
            menuPortalTarget={document.body}
            menuPlacement="auto"
          />
        )}
      />

      <ControlledFormField
        name="regionIds"
        renderInput={({ field }) => (
          <RetailerRegionSelect
            {...field}
            isMulti
            menuPortalTarget={document.body}
            menuPlacement="auto"
          />
        )}
      />

      {isAdmin && (
        <FormField
          name="affiliation"
          isDisabled={retailerType !== RetailerType.INDEPENDENT}
        />
      )}

      <ControlledFormField
        name="slottingRequired"
        renderInput={({ field }) => (
          <RadioButtonOptionsSelect
            options={slottingOptions}
            labels={slottingOptionsMap}
            size="sm"
            {...field}
          />
        )}
      />

      <ControlledFormField
        name="pricingStrategy"
        renderInput={({ field }) => (
          <RadioButtonOptionsSelect
            options={pricingStrategies}
            labels={pricingStrategyMap}
            size="sm"
            {...field}
          />
        )}
      />

      <ControlledFormField
        name="categoryReview"
        renderInput={({ field }) => (
          <RadioButtonOptionsSelect
            options={retailerCategories}
            labels={retailerCategoryReviewMap}
            size="sm"
            {...field}
          />
        )}
      />

      <AddressFieldset<RetailerFormFields>
        fieldNamePrefix="headquartersAddress"
        legendLabel="Headquarters Location"
        mt={10}
      />

      <ControlledFormField
        name="headquartersPhoneNumber.phone"
        label="Headquarters Phone"
        renderInput={({ field }) => <PhoneInput {...field} />}
        mt={12}
      />

      <ControlledFormField
        name="distributionCenterIds"
        renderInput={({ field }) => (
          <DistributionCenterSelect
            {...field}
            isAddedToBrand={!isAdmin}
            isAdmin={isAdmin}
            isMulti
            menuPortalTarget={document.body}
            menuPlacement="auto"
          />
        )}
        mt={12}
      />

      {isAdmin && (
        <ControlledFormField
          name="brokerIds"
          renderInput={({ field }) => <BrokerSelect {...field} isMulti />}
          helpText={
            <>All brokers that cover this retailer should be added here.</>
          }
        />
      )}

      <ControlledFormField
        name="tags"
        renderInput={({ field: { value, ...field } }) => (
          <RetailerTagsSelect
            {...field}
            value={(value || []) as string[]}
            isMulti
          />
        )}
      />

      <FormField
        name="notes"
        helpText={isAdmin ? "Notes are not visible to brands." : undefined}
        InputComponent={Textarea}
      />
    </Form>
  );
}
